// src/components/CaseView/CallQuickTap.tsx
// ──────────────────────────────────────────────────────────────
// Quick-tap buttons for manual nudge triggers during P2P calls.
// Labels describe what the reviewer said, not abstract categories.
// Physician hears something → scans for match → taps → gets cue.
// One cognitive step: pattern matching.
//
// Designed to render as an embedded dock inside CallTranscription
// via the bottomSlot prop — not as a standalone card.
// ──────────────────────────────────────────────────────────────

import { useState, useCallback } from "react";
import type { NudgeType } from "../../hooks/useCallNudge";
import { QUICK_TAP_BUTTONS } from "../../hooks/useCallNudge";

interface CallQuickTapProps {
  onTap: (type: NudgeType) => void;
  tappedTypes: Set<NudgeType>; // Types already triggered this call
}

export function CallQuickTap({ onTap, tappedTypes }: CallQuickTapProps) {
  const [flashType, setFlashType] = useState<NudgeType | null>(null);

  const handleTap = useCallback(
    (type: NudgeType) => {
      onTap(type);
      // Brief visual confirmation — button flashes then settles to "tapped" state
      setFlashType(type);
      setTimeout(() => setFlashType(null), 600);
    },
    [onTap]
  );

  return (
    <div className="px-4 py-3 space-y-2">
      {/* Section label — minimal, doesn't demand attention */}
      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        Hearing this? Tap for guidance
      </span>

      {/* Button grid — 3 columns, 2 rows, all visible without scrolling */}
      <div className="grid grid-cols-3 gap-1.5">
        {QUICK_TAP_BUTTONS.map((btn) => {
          const isTapped = tappedTypes.has(btn.type);
          const isFlashing = flashType === btn.type;

          return (
            <button
              key={btn.type}
              onClick={() => handleTap(btn.type)}
              title={btn.detail}
              className={`
                relative px-2 py-2.5 rounded-lg text-left
                transition-all duration-200 ease-out
                border
                ${
                  isFlashing
                    ? "bg-teal-500/20 border-teal-500/40 scale-[0.97]"
                    : isTapped
                    ? "bg-slate-800/30 border-slate-700/30 opacity-50"
                    : "bg-slate-800/50 border-slate-700/40 hover:border-slate-600/60 hover:bg-slate-700/40 active:scale-[0.97]"
                }
              `}
            >
              {/* Label — what the reviewer said */}
              <span
                className={`
                  text-xs font-medium leading-tight block
                  ${
                    isFlashing
                      ? "text-teal-300"
                      : isTapped
                      ? "text-slate-500"
                      : "text-slate-300"
                  }
                `}
              >
                {btn.label}
              </span>

              {/* Tapped indicator */}
              {isTapped && !isFlashing && (
                <span className="absolute top-1 right-1.5 text-[9px] text-slate-600">
                  ✓
                </span>
              )}

              {/* Flash confirmation */}
              {isFlashing && (
                <span className="absolute top-1 right-1.5 text-[9px] text-teal-400 font-medium">
                  ✓
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}